import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'
import { useVirtualizer, type Range } from '@tanstack/react-virtual'
import { Mic, Monitor } from 'lucide-react'
import { Tooltip } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { Message } from './types'
import { SpeakerRenameProvider, SpeakerRenameTrigger } from './speaker-rename-popover'
import { useFindInPageRangeExtractor } from '../../hooks/use-find-in-page-mount-all'
import { READING_ANCHOR_ATTRIBUTE, readingAnchorId } from '../../hooks/reading-anchor'
import { mergeSpeakerTurns } from '@renderer/utils/audio-note-helpers'
import type { ShownPartial } from '@renderer/hooks/audio/use-held-partial'
import { AUDIO_SOURCE_LABELS, type AudioSource } from './audio-source'
import { isPendingTurn, placeLivePartial } from './live-partial'

/**
 * Matches auto-generated speaker labels:
 *   - "Speaker A" / "Speaker 1" (untitled)
 *   - "{Name}'s mic — A" (legacy / stamped mic)
 *   - "My mic — A" (neutral mic pre-resolution)
 */
const UNNAMED_SPEAKER_PATTERN = /^Speaker [A-Z\d]+$|.+'s mic [—-] [A-Z]$|^My mic [—-] [A-Z]$/
/** Mic speakers include: "Speaker A", "{Name}'s mic — A", "My mic — A" */
const MIC_SPEAKER_PATTERN = /^Speaker [A-Z]$|.+'s mic [—-] [A-Z]$|^My mic [—-] [A-Z]$/

/**
 * Prefer the entry's `isHost` flag for the mic/system icon — it's the canonical
 * signal and survives label rewrites (raw Deepgram view, upstream labeller bugs
 * like the Recall SpeakerTracker that hardcodes `isHost: false`). Fall back to
 * the label pattern only when `isHost` is unknown (older persisted entries).
 */
function audioSourceOfMessage(message: Message): AudioSource {
  if (message.isHost === true) return 'microphone'
  if (message.isHost === false) return 'system'
  return MIC_SPEAKER_PATTERN.test(message.sender) ? 'microphone' : 'system'
}

type MeetingTranscriptProps = {
  messages: Array<Message>
  className?: string
  style?: CSSProperties
  emptyState?: ReactNode
  containerRef?: RefObject<HTMLDivElement>
  endRef?: RefObject<HTMLDivElement>
  /**
   * Virtualize against `scrollElement`. Explicit rather than inferred from
   * `scrollElement` being set: callers own a scroll node via a callback ref, so
   * it is legitimately `null` for the first render, and treating that as "plain
   * list" would mount every row of a long transcript for one frame before
   * switching modes.
   */
  virtualize?: boolean
  /** The scroller to measure against. `null` until the caller's ref attaches. */
  scrollElement?: HTMLDivElement | null
  /** Scrolls to and highlights the turn spoken at `time`; a new object re-jumps to the same time. */
  jumpTo?: TranscriptJump | null
  /**
   * The scroller holds the reader's place itself (`useBodyScroll`'s
   * `followGrowth`), so the virtualizer must not also correct for rows
   * resizing above the viewport: both would apply the same shift.
   */
  holdsReadingPosition?: boolean
  /** The live in-flight line, streamed onto the transcript (see `placeLivePartial`). */
  livePartial?: ShownPartial
  children?: ReactNode
}

export interface TranscriptJump {
  time: string
}

/** The last turn that started at or before `time`, so a jump lands on what was being said then. */
function transcriptIndexAt(messages: Array<Message>, time: string): number {
  const target = new Date(time).getTime()
  let index = 0
  for (let i = 0; i < messages.length; i++) {
    if (new Date(messages[i].time).getTime() > target) break
    index = i
  }
  return index
}

function AudioSourceIcon({ source }: { source: AudioSource }) {
  const label = AUDIO_SOURCE_LABELS[source]
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          // The before: box widens the 12px glyph to a 24px hover target without moving the header text
          <span
            role="img"
            aria-label={label}
            className="text-foreground-placeholder relative ml-1 inline-flex items-center align-middle before:absolute before:-inset-1.5"
          />
        }
      >
        {source === 'microphone' ? <Mic className="size-3" /> : <Monitor className="size-3" />}
      </Tooltip.Trigger>
      <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
  )
}

type TranscriptMessageProps = { message: Message; showHeader: boolean }

/**
 * `activeTranscriptMessagesAtom` rebuilds every `Message` from scratch on each
 * transcript delta (`mapTranscriptEntriesToMessages` constructs new objects), so
 * reference equality never holds and a default `memo` bails out for nobody —
 * every visible row re-renders per delta, and each header row drags a
 * `SpeakerRenamePopover` with it. Compare the fields the row actually renders
 * instead, the same way `areMessagePropsEqual` (chat/components/chat/message/comparators.ts) does for chat messages.
 */
function areTranscriptMessagePropsEqual(prev: TranscriptMessageProps, next: TranscriptMessageProps): boolean {
  if (prev.showHeader !== next.showHeader) return false
  const a = prev.message
  const b = next.message
  return (
    a.sender === b.sender &&
    a.text === b.text &&
    a.pendingText === b.pendingText &&
    a.displayTime === b.displayTime &&
    a.isHost === b.isHost &&
    (a.dgSpeakerIndices?.join(',') ?? '') === (b.dgSpeakerIndices?.join(',') ?? '') &&
    a.segments.length === b.segments.length &&
    a.segments.every((segment, index) => segment.time === b.segments[index].time)
  )
}

const TranscriptMessage = memo(({ message, showHeader }: TranscriptMessageProps) => {
  // A pending turn's "You"/"Them" names nobody yet, so there's no speaker to rename
  const isRenamable = message.sender !== 'You' && !isPendingTurn(message)
  return (
    <div className={twMerge('flex w-full flex-col items-start justify-start self-stretch', showHeader ? 'gap-1' : '')}>
      {showHeader && (
        <div className="text-foreground type-ui-sm select-text self-stretch font-medium capitalize">
          {isRenamable ? (
            <SpeakerRenameTrigger speakerName={message.sender} dgSpeakerIndices={message.dgSpeakerIndices}>
              {message.sender}
            </SpeakerRenameTrigger>
          ) : (
            message.sender
          )}
          {message.sender !== 'You' &&
            (message.isHost !== undefined || UNNAMED_SPEAKER_PATTERN.test(message.sender)) && (
              <AudioSourceIcon source={audioSourceOfMessage(message)} />
            )}
          <span className="text-foreground-secondary ml-1.5 font-normal tabular-nums">{message.displayTime}</span>
        </div>
      )}
      <div className="text-foreground type-prose-sm cursor-text select-text self-stretch font-normal">
        {message.segments.map((segment, index) => (
          <Fragment key={`${segment.time}:${index}`}>
            {index > 0 && ' '}
            <span {...{ [READING_ANCHOR_ATTRIBUTE]: segment.time }}>{segment.text}</span>
          </Fragment>
        ))}
        {message.pendingText ? (
          <span className="text-foreground-secondary select-none">
            {message.segments.length > 0 && ' '}
            {message.pendingText}
          </span>
        ) : null}
      </div>
    </div>
  )
}, areTranscriptMessagePropsEqual)

TranscriptMessage.displayName = 'TranscriptMessage'

function transcriptMessageKey(message: Message, index: number): string {
  const indices = message.dgSpeakerIndices
  if (indices && indices.length > 0) {
    return `${message.time}:${indices.join(',')}`
  }
  // Without DG indices there is nothing to disambiguate two adjacent groups from
  // the same speaker that share a `time`, and a duplicate key poisons the
  // virtualizer's `measureElement` cache — the row then renders at its
  // neighbour's height. Fall back to the position, which is stable for the
  // append-only live transcript.
  return `${message.time}:${message.isHost ?? 'unknown'}:${message.sender}:${index}`
}

function TranscriptRow({
  message,
  previousMessage,
  highlighted = false,
}: {
  message: Message
  previousMessage?: Message
  highlighted?: boolean
}) {
  const showHeader = !previousMessage || previousMessage.sender !== message.sender
  const content = <TranscriptMessage message={message} showHeader={showHeader} />
  return (
    <div className={twMerge(previousMessage && (showHeader ? 'pt-6' : 'pt-1.5'))}>
      {highlighted ? <div className="bg-element rounded-md">{content}</div> : content}
    </div>
  )
}

const neverAdjustScrollPosition = () => false

function TranscriptMessages({
  messages,
  virtualize,
  scrollElement,
  jumpTo,
  holdsReadingPosition,
}: {
  messages: Array<Message>
  virtualize?: boolean
  scrollElement?: HTMLDivElement | null
  jumpTo?: TranscriptJump | null
  holdsReadingPosition?: boolean
}) {
  const listRef = useRef<HTMLDivElement>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const findInPageRangeExtractor = useFindInPageRangeExtractor()
  // Keeps the row holding the reader's place mounted. A regroup re-keys the rows
  // around it, and the virtualizer would otherwise pick the next range from the
  // old scrollTop against new estimated heights, leaving nothing on screen for
  // the hold to find.
  const rangeExtractor = useCallback(
    (range: Range) => {
      const indexes = findInPageRangeExtractor(range)
      const anchorId = holdsReadingPosition ? readingAnchorId(scrollElement) : undefined
      if (!anchorId) return indexes
      const anchorIndex = messages.findIndex((message) => message.segments.some((segment) => segment.time === anchorId))
      if (anchorIndex === -1 || indexes.includes(anchorIndex)) return indexes
      return [...indexes, anchorIndex].sort((a, b) => a - b)
    },
    [findInPageRangeExtractor, holdsReadingPosition, scrollElement, messages],
  )

  const measureScrollMargin = useCallback(() => {
    const list = listRef.current
    if (!list || !scrollElement) {
      return
    }
    const nextMargin =
      list.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top + scrollElement.scrollTop
    setScrollMargin((current) => (current === nextMargin ? current : nextMargin))
  }, [scrollElement])

  useLayoutEffect(() => {
    measureScrollMargin()
    const list = listRef.current
    if (!list || !scrollElement) {
      return
    }
    const observer = new ResizeObserver(measureScrollMargin)
    observer.observe(list)
    observer.observe(scrollElement)
    // Content inserted above the list moves it without resizing either of them
    if (scrollElement.firstElementChild) observer.observe(scrollElement.firstElementChild)
    return () => observer.disconnect()
  }, [measureScrollMargin, scrollElement])

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollElement ?? null,
    // Deliberate underestimates (real: header ≈67px, continuation ≈27px, first
    // row ≈43px). Live rows append into a bottom-pinned scroller and the pin
    // lands on the estimated scrollHeight, so an overestimate shrinks the
    // document when measurement corrects it and the browser clamps scrollTop.
    // Undershooting only grows the document below the pin, which leaves the
    // viewport where it is.
    estimateSize: (index) => {
      if (index === 0) return 40
      const showHeader = messages[index - 1]?.sender !== messages[index]?.sender
      return showHeader ? 64 : 26
    },
    getItemKey: (index) => transcriptMessageKey(messages[index], index),
    overscan: 10,
    rangeExtractor,
    scrollMargin,
  })
  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = holdsReadingPosition ? neverAdjustScrollPosition : undefined

  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  // Re-run once the transcript arrives, so a jump clicked before it loaded still lands
  const hasMessages = messages.length > 0
  useEffect(() => {
    if (!jumpTo || !hasMessages) return
    const index = transcriptIndexAt(messagesRef.current, jumpTo.time)
    // Centered, so the captured list pinned over the top edge can't cover it
    virtualizer.scrollToIndex(index, { align: 'center' })
    setHighlightedIndex(index)
  }, [jumpTo, hasMessages, virtualizer])

  if (!virtualize) {
    return (
      <>
        {messages.map((message, index) => (
          <TranscriptRow
            key={transcriptMessageKey(message, index)}
            message={message}
            previousMessage={messages[index - 1]}
          />
        ))}
      </>
    )
  }

  return (
    <div ref={listRef} className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const message = messages[virtualRow.index]
        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            className="absolute left-0 top-0 w-full"
            style={{ transform: `translateY(${virtualRow.start - scrollMargin}px)` }}
          >
            <TranscriptRow
              message={message}
              previousMessage={messages[virtualRow.index - 1]}
              highlighted={virtualRow.index === highlightedIndex}
            />
          </div>
        )
      })}
    </div>
  )
}

export function MeetingTranscript({
  messages,
  className,
  style,
  emptyState,
  containerRef,
  endRef,
  virtualize,
  scrollElement,
  jumpTo,
  holdsReadingPosition,
  livePartial,
  children,
}: MeetingTranscriptProps) {
  const mergedTurns = useMemo(() => mergeSpeakerTurns(messages), [messages])
  const turns = useMemo(() => placeLivePartial(mergedTurns, livePartial), [mergedTurns, livePartial])
  return (
    <div
      ref={containerRef}
      className={twMerge(
        'flex w-full flex-1 flex-col items-start justify-start gap-1 overflow-y-auto py-0.5',
        className,
      )}
      style={style}
    >
      {/* One rename combobox for the whole transcript, anchored to whichever row
          was clicked — rather than a Base UI menu root and styled trigger button
          per header row. Only the (stable) open callback reaches the rows, so
          opening it re-renders nothing in the list. */}
      <SpeakerRenameProvider>
        <TranscriptMessages
          messages={turns}
          virtualize={virtualize}
          scrollElement={scrollElement}
          jumpTo={jumpTo}
          holdsReadingPosition={holdsReadingPosition}
        />
        {children}
        {turns.length === 0 ? emptyState : null}
        <div ref={endRef} />
      </SpeakerRenameProvider>
    </div>
  )
}
